import { MbrsStatusEnum } from '../../types/commons';

/**
 * AS-842 — Centralised allow-lists for row interactivity, replacing the old
 * `!== InProgress` checks scattered across the listing template.
 *
 * Rules (per spec):
 *   - Company-name link / more-actions menu visible only when row is in a
 *     frontend-owned terminal state (Success / SuccessWithWarnings / Review).
 *   - Download enabled only on clean terminals (Success / SuccessWithWarnings)
 *     — NOT on Review, which means the user must amend before download. This
 *     makes the gate implicitly phase-aware: a Phase 1 conversion never
 *     reaches a Success terminal (BE-confirmed; see the isPhase1/isPhase2
 *     note in commons.ts), so a Phase 1 row is never downloadable and no
 *     explicit phase check is needed here.
 *   - Delete enabled on any terminal — same set as the menu visibility above.
 *   - InProgress / PendingValidation / Failed / AwaitingFiles: no
 *     interactivity. PendingValidation is treated identically to InProgress
 *     because the user sees the same "still working" chip and the row's true
 *     status is being computed silently.
 *
 * The `allowNonSuccess` parameter on `isMoreActionButtonVisible` /
 * `isDownloadable` relaxes the download half of those rules for QA — see
 * `RuntimeConfig.nonSuccessDownloadEnabled`. It is threaded in as an argument
 * rather than read from `RuntimeConfigService` in here so this module stays a
 * pure rule table with no DI and no import cycle into the services layer.
 *
 * Keep these `Set`s instead of switch statements so future status additions
 * are a one-line change in one file.
 */
const TERMINAL_INTERACTIVE: ReadonlySet<MbrsStatusEnum> = new Set([
  MbrsStatusEnum.Success,
  MbrsStatusEnum.SuccessWithWarnings,
  MbrsStatusEnum.Review,
]);

const DOWNLOADABLE: ReadonlySet<MbrsStatusEnum> = new Set([
  MbrsStatusEnum.Success,
  MbrsStatusEnum.SuccessWithWarnings,
]);

/**
 * Statuses that become downloadable when the runtime-config flag
 * `nonSuccessDownloadEnabled` is on — see the `allowNonSuccess` parameter
 * below and `RuntimeConfig.nonSuccessDownloadEnabled` for the why.
 *
 * `Review` and `Failed` only. The still-working / artifact-less statuses
 * (`InProgress`, `PendingValidation`, `AwaitingFiles`) are excluded
 * deliberately and permanently: the conversion has not produced anything to
 * download yet, so widening to them would offer an affordance that can only
 * fail. Do not "complete the set" here.
 */
const NON_SUCCESS_DOWNLOADABLE: ReadonlySet<MbrsStatusEnum> = new Set([
  MbrsStatusEnum.Review,
  MbrsStatusEnum.Failed,
]);

/**
 * Statuses whose more-actions menu only appears when the same flag is on.
 *
 * `Review` is absent because it is already in `TERMINAL_INTERACTIVE` — its
 * menu always renders; the flag only un-greys the Download row inside it.
 * `Failed` has no menu at all by default, so without this the widened
 * download would be unreachable.
 */
const NON_SUCCESS_MENU_VISIBLE: ReadonlySet<MbrsStatusEnum> = new Set([
  MbrsStatusEnum.Failed,
]);

export const isCompanyClickable = (status: MbrsStatusEnum): boolean =>
  TERMINAL_INTERACTIVE.has(status);

/**
 * @param allowNonSuccess Value of the runtime-config flag
 *   `nonSuccessDownloadEnabled`, passed in by the caller rather than read
 *   here so this module stays a pure, DI-free rule table. Defaults to
 *   `false`, i.e. the behaviour that predates the flag.
 */
export const isMoreActionButtonVisible = (
  status: MbrsStatusEnum,
  allowNonSuccess = false,
): boolean =>
  TERMINAL_INTERACTIVE.has(status)
  || (allowNonSuccess && NON_SUCCESS_MENU_VISIBLE.has(status));

/**
 * @param allowNonSuccess See `isMoreActionButtonVisible`. When `true`,
 *   `Review` and `Failed` rows also become downloadable.
 */
export const isDownloadable = (
  status: MbrsStatusEnum,
  allowNonSuccess = false,
): boolean =>
  DOWNLOADABLE.has(status)
  || (allowNonSuccess && NON_SUCCESS_DOWNLOADABLE.has(status));

export const isDeletable = (status: MbrsStatusEnum): boolean =>
  TERMINAL_INTERACTIVE.has(status);

/**
 * "Still working" check used by the chip-rendering / toast-suppression paths.
 * Both InProgress (backend extracting) and PendingValidation (frontend
 * silently validating) are user-visually identical — the listing renders
 * "In Progress" for both.
 */
export const isInProgressLike = (status: MbrsStatusEnum): boolean =>
  status === MbrsStatusEnum.InProgress ||
  status === MbrsStatusEnum.PendingValidation;
